import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FileText,
  Search,
  UploadCloud,
  Users,
  Trash2,
  MoreHorizontal,
  LayoutGrid,
  List as ListIcon,
  Archive,
  Copy as CopyIcon,
  X,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";

const TABS = [
  { value: "all", label: "All" },
  { value: "draft", label: "Drafts" },
  { value: "sent", label: "Sent" },
  { value: "partially_signed", label: "In progress" },
  { value: "completed", label: "Completed" },
  { value: "declined", label: "Declined" },
];

export default function Documents() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [folder, setFolder] = useState("");
  const [tag, setTag] = useState("");
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [selected, setSelected] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/documents", {
        params: {
          status: status === "all" ? undefined : status,
          q: q || undefined,
          folder: folder || undefined,
          tag: tag || undefined,
        },
      });
      setDocs(data);
      setSelected(new Set());
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [status, folder, tag]);

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const selectAll = () => {
    if (selected.size === docs.length) setSelected(new Set());
    else setSelected(new Set(docs.map((d) => d.id)));
  };

  const bulk = async (action, extra = {}) => {
    if (selected.size === 0) return;
    try {
      await api.post("/documents/bulk", {
        document_ids: Array.from(selected),
        action,
        ...extra,
      });
      toast.success(`${selected.size} document(s) ${action}d`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm("Delete this document?")) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const duplicate = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const { data } = await api.post(`/documents/${id}/duplicate`);
      toast.success("Duplicated");
      navigate(`/app/documents/${data.id}/prepare`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-7xl mx-auto" data-testid="documents-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium">
            Documents
          </div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight">All agreements</h1>
          <p className="mt-1 text-sm text-slate-500">
            Track, manage, and complete every document in one place.
          </p>
        </div>
        <Button
          onClick={() => navigate("/app/documents/upload")}
          className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
          data-testid="documents-upload-btn"
        >
          <UploadCloud className="h-4 w-4" /> Upload document
        </Button>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:flex-wrap min-w-0">
        <div className="overflow-x-auto -mx-2 px-2 sm:mx-0 sm:px-0 sm:overflow-visible min-w-0 w-full sm:w-auto">
          <Tabs value={status} onValueChange={setStatus}>
            <TabsList className="w-max">
              {TABS.map((t) => (
                <TabsTrigger key={t.value} value={t.value} data-testid={`tab-${t.value}`}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            load();
          }}
          className="relative w-full sm:flex-1 sm:max-w-md sm:ml-auto"
        >
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by title…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-10"
            data-testid="documents-search-input"
          />
        </form>

        <div className="flex items-center gap-2 flex-wrap">
          <Input
            placeholder="Folder"
            value={folder}
            onChange={(e) => setFolder(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="h-10 w-32"
            data-testid="documents-folder-filter"
          />
          <Input
            placeholder="Tag"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="h-10 w-28"
            data-testid="documents-tag-filter"
          />
          {(folder || tag) && (
            <button
              onClick={() => {
                setFolder("");
                setTag("");
              }}
              className="text-xs text-slate-500 hover:text-slate-900 underline"
              data-testid="documents-clear-filters"
            >
              Clear
            </button>
          )}

          <div className="hidden sm:flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5">
            <button
              onClick={() => setView("list")}
              className={`p-1.5 rounded-md ${view === "list" ? "bg-white dark:bg-slate-900 shadow-sm" : ""}`}
              data-testid="view-list"
            >
              <ListIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => setView("grid")}
              className={`p-1.5 rounded-md ${view === "grid" ? "bg-white dark:bg-slate-900 shadow-sm" : ""}`}
              data-testid="view-grid"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div
          className="mt-4 rounded-lg border border-slate-900 dark:border-slate-700 bg-slate-900 dark:bg-slate-800 text-white px-3 sm:px-4 py-3"
          data-testid="bulk-action-bar"
        >
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-sm font-medium">{selected.size} selected</div>
            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
              <Button
                size="sm"
                variant="outline"
                onClick={() => bulk("archive")}
                className="bg-transparent border-white/20 text-white hover:bg-white/10 h-8 text-xs sm:text-sm"
                data-testid="bulk-archive"
              >
                <Archive className="h-3.5 w-3.5 sm:mr-1" />
                <span className="hidden sm:inline">Archive</span>
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const folderName = prompt("Move to folder name:");
                  if (folderName) bulk("move", { folder: folderName });
                }}
                className="bg-transparent border-white/20 text-white hover:bg-white/10 h-8 text-xs sm:text-sm"
              >
                Move
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const t = prompt("Add tag:");
                  if (t) bulk("tag", { tag: t });
                }}
                className="bg-transparent border-white/20 text-white hover:bg-white/10 h-8 text-xs sm:text-sm"
              >
                + Tag
              </Button>
              <button
                onClick={() => setSelected(new Set())}
                className="p-1.5 hover:bg-white/10 rounded"
                aria-label="Clear selection"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!loading && docs.length === 0 && (
        <div className="mt-6 p-16 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-center">
          <FileText className="h-10 w-10 text-slate-400 mx-auto" />
          <div className="mt-4 text-base font-medium">No documents found</div>
          <div className="text-sm text-slate-500 mt-1">
            Upload your first document to start signing.
          </div>
          <Button
            onClick={() => navigate("/app/documents/upload")}
            className="mt-6 bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            Upload document
          </Button>
        </div>
      )}

      {/* List view */}
      {view === "list" && docs.length > 0 && (
        <div className="mt-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
          {/* Desktop table header */}
          <div className="hidden md:grid grid-cols-[36px_1fr_120px_140px_120px_60px] px-5 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40 text-[11px] uppercase tracking-wider text-slate-500 font-medium">
            <div className="flex items-center">
              <Checkbox
                checked={selected.size > 0 && selected.size === docs.length}
                onCheckedChange={selectAll}
                data-testid="select-all"
              />
            </div>
            <div>Document</div>
            <div>Recipients</div>
            <div>Status</div>
            <div>Created</div>
            <div></div>
          </div>
          {docs.map((d) => (
            <div
              key={d.id}
              className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40"
              data-testid={`doc-row-${d.id}`}
            >
              {/* Desktop row */}
              <div className="hidden md:grid grid-cols-[36px_1fr_120px_140px_120px_60px] items-center px-5 py-4">
                <div onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selected.has(d.id)}
                    onCheckedChange={() => toggleSelect(d.id)}
                    data-testid={`select-${d.id}`}
                  />
                </div>
                <Link to={`/app/documents/${d.id}`} className="flex items-center gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-md bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
                    <FileText className="h-4 w-4 text-slate-600 dark:text-slate-300" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{d.title}</div>
                    <div className="text-xs text-slate-500 truncate">{d.file_name}</div>
                  </div>
                </Link>
                <div className="text-sm text-slate-600 dark:text-slate-300 flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5" />
                  {d.recipients?.length ?? 0}
                </div>
                <div>
                  <StatusBadge status={d.status} />
                </div>
                <div className="text-xs font-mono text-slate-500">
                  {new Date(d.created_at).toLocaleDateString()}
                </div>
                <div className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        onClick={(e) => e.stopPropagation()}
                        className="p-2 rounded-md hover:bg-slate-200 dark:hover:bg-slate-700"
                        data-testid={`doc-actions-${d.id}`}
                      >
                        <MoreHorizontal className="h-4 w-4 text-slate-500" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => duplicate(d.id, e)}>
                        <CopyIcon className="h-4 w-4 mr-2" /> Duplicate
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => remove(d.id, e)}
                        className="text-red-600"
                        data-testid={`doc-delete-${d.id}`}
                      >
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Mobile card row */}
              <div className="md:hidden flex items-start gap-3 px-4 py-4">
                <div onClick={(e) => e.stopPropagation()} className="pt-0.5">
                  <Checkbox
                    checked={selected.has(d.id)}
                    onCheckedChange={() => toggleSelect(d.id)}
                    data-testid={`select-mobile-${d.id}`}
                  />
                </div>
                <Link to={`/app/documents/${d.id}`} className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{d.title}</div>
                      <div className="text-xs text-slate-500 truncate mt-0.5">{d.file_name}</div>
                    </div>
                    <StatusBadge status={d.status} />
                  </div>
                  <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500 font-mono">
                    <span className="inline-flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {d.recipients?.length ?? 0}
                    </span>
                    <span>·</span>
                    <span>{new Date(d.created_at).toLocaleDateString()}</span>
                  </div>
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      onClick={(e) => e.stopPropagation()}
                      className="p-2 -mr-2 rounded-md hover:bg-slate-200"
                    >
                      <MoreHorizontal className="h-4 w-4 text-slate-500" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={(e) => duplicate(d.id, e)}>
                      <CopyIcon className="h-4 w-4 mr-2" /> Duplicate
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => remove(d.id, e)} className="text-red-600">
                      <Trash2 className="h-4 w-4 mr-2" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Grid view */}
      {view === "grid" && docs.length > 0 && (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="docs-grid">
          {docs.map((d) => (
            <Link
              key={d.id}
              to={`/app/documents/${d.id}`}
              className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 hover:shadow-md transition relative"
              data-testid={`doc-card-${d.id}`}
            >
              <div onClick={(e) => e.preventDefault()} className="absolute top-3 left-3">
                <Checkbox
                  checked={selected.has(d.id)}
                  onCheckedChange={() => toggleSelect(d.id)}
                />
              </div>
              <div className="flex items-start justify-between pl-7">
                <div className="h-10 w-10 rounded-md bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-slate-600 dark:text-slate-300" />
                </div>
                <StatusBadge status={d.status} />
              </div>
              <h3 className="mt-4 font-semibold line-clamp-1">{d.title}</h3>
              <div className="text-xs text-slate-500 truncate">{d.file_name}</div>
              <div className="mt-4 flex items-center gap-3 text-xs text-slate-500 font-mono">
                <span className="inline-flex items-center gap-1">
                  <Users className="h-3 w-3" /> {d.recipients?.length ?? 0}
                </span>
                <span>·</span>
                <span>{new Date(d.created_at).toLocaleDateString()}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
